import { Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { ExpandableConfig } from 'antd/es/table/interface'
import React, { FunctionComponent, useCallback, useMemo } from 'react'

import {
  AuctionLinkedPropertyTableView,
  AuctionMonitoringFilterState,
  AuctionMonitoringRecord,
} from '../../../common/auctionMonitoringTypes'
import {
  AUCTION_STATES,
  AUCTION_TYPES,
} from '../../../common/auctionScrapeTypes'
import { AuctionListingFilter } from '../../../graphql/generated'
import { setAuctionDrawer } from '../../../redux/drawerSlice'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import {
  auctionDecreeColumnTitle,
  createLinkedPropertiesRenderer,
  renderAuctionDecreeUrl,
  renderAuctionedOn,
  renderEndDate,
  renderInfo,
  renderPrice,
  renderStartDate,
  renderState,
} from '../../utils/auctionMonitoringTableHelpers'
import {
  labelSorter,
  numberSorter,
  stringSorter,
  utcDescDateSorter,
} from '../../utils/generalTableHelpers'
import { sparsePagination } from '../../utils/layoutConst'
import { useColumnFilters } from '../../utils/useColumnFilters'
import { useColumnOptionFilter } from '../../utils/useColumnOptionFilter'
import { useDeclension } from '../../utils/useDeclension'
import { useHandleExpandRow } from '../../utils/useHandleExpandRow'
import { seenRowClassName } from '../../utils/userActivityLogHelpers'
import { useTableLocale } from '../../utils/useTableLocale'
import { useUniqueValues } from '../../utils/useUniqueValues'
import { AuctionMonitoringLinkedPropertyTableContainer } from '../AuctionMonitoringLinkedPropertyTableContainer/AuctionMonitoringLinkedPropertyTableContainer'
import { AuctionMonitoringTableSummary } from '../AuctionMonitoringTableSummary/AuctionMonitoringTableSummary'
import { ExpandColumnTitle } from '../ExpandColumnTitle/ExpandColumnTitle'
import { ExpandIcon } from '../ExpandIcon/ExpandIcon'
import { TableCellLabels } from '../TableCellLabels/TableCellLabels'

export interface AuctionMonitoringTableProps {
  data?: AuctionMonitoringRecord[] | null
  filter?: AuctionMonitoringFilterState
  auctionListingFilter?: AuctionListingFilter
  viewType: AuctionLinkedPropertyTableView
  activePropertyId?: string
  loading?: boolean
}

const tableId = 'auction-monitoring'
const scrollX = { x: 1200 }

const AuctionMonitoringTable: FunctionComponent<AuctionMonitoringTableProps> =
  ({
    filter,
    loading,
    data,
    viewType,
    activePropertyId,
    auctionListingFilter,
  }) => {
    const dispatch = useAppDispatch()
    const decline = useDeclension()

    const handleExpandRow = useHandleExpandRow(tableId)
    const expandedRowKeys = useAppSelector(
      state => state.expandedRow.tables?.[tableId] || []
    )

    const { searchFilter, dateFilter, rangeFilter, labelFilter } =
      useColumnFilters<AuctionMonitoringRecord>()

    const auctionedOnOptions = useUniqueValues(data || [], 'auctionedOn')
    const { getColumnOptionFilter } =
      useColumnOptionFilter<AuctionMonitoringRecord>(auctionedOnOptions)

    const setDrawer = useCallback(
      (id: string, type: AUCTION_TYPES) =>
        dispatch(setAuctionDrawer({ id: id, auctionType: type })),
      [dispatch]
    )

    const rowClassName = useCallback(
      (record: AuctionMonitoringRecord) => {
        const isExpanded = expandedRowKeys.includes(record.id)
          ? 'rowExpanded fade-in'
          : ''
        const isSeen = seenRowClassName(
          record.activityLogs?.nodes?.[0]?.editedAt,
          record.updatedAt || record.createdAt
        )
        return [isExpanded, isSeen].join(' ')
      },
      [expandedRowKeys]
    )

    const renderTitle = useCallback(
      (title: string, record: AuctionMonitoringRecord) => (
        <a
          onClick={() => setDrawer(record.id, record.type)}
          title="Zobrazit náhled dražby"
        >
          {title}
        </a>
      ),
      [setDrawer]
    )
    const renderLinkedProperties = useCallback(
      (_: string, record: AuctionMonitoringRecord) => {
        const title = decline(
          record?.linkedProperties?.totalCount,
          'property',
          true
        )
        return createLinkedPropertiesRenderer(record, handleExpandRow, title)
      },
      [handleExpandRow, decline]
    )

    const columns = useMemo<ColumnsType<AuctionMonitoringRecord>>(
      () => [
        {
          title: 'Dat. dražby',
          dataIndex: 'startDate',
          className: 'w-36',
          sorter: utcDescDateSorter('startDate'),
          ...dateFilter('startDate'),
          render: renderStartDate,
        },
        ...(filter?.state?.includes(AUCTION_STATES.ENDED)
          ? [
              {
                title: 'Dat. ukončení',
                dataIndex: 'endDate',
                className: 'w-40',
                sorter: utcDescDateSorter('endDate'),
                ...dateFilter('endDate'),
                render: renderEndDate,
              },
            ]
          : []),
        {
          title: 'Cena',
          dataIndex: 'price',
          className: 'w-32 !text-right',
          sorter: numberSorter('price'),
          render: renderPrice,
        },
        {
          title: 'Štítek',
          dataIndex: 'labels',
          sorter: labelSorter('labels'),
          ...labelFilter('labels'),
          render: (labels, record) => (
            <TableCellLabels
              labels={labels}
              labelEntityType="auctionListingId"
              recordId={record.id}
              modalTitle={record.title}
            />
          ),
        },
        {
          title: 'Předmět dražby',
          dataIndex: 'title',
          sorter: stringSorter('title'),
          ...searchFilter('title', 'Hledat v předmětu dražby'),
          render: renderTitle,
        },
        {
          title: 'Přiřazené nemovitosti',
          dataIndex: 'id',
          key: 'properties',
          ...rangeFilter(['linkedProperties', 'totalCount']),
          render: renderLinkedProperties,
        },
        {
          title: 'Odkaz do dražby',
          dataIndex: 'auctionedOn',
          sorter: stringSorter('auctionedOn'),
          ...getColumnOptionFilter('auctionedOn'),
          render: renderAuctionedOn,
        },
        {
          title: auctionDecreeColumnTitle,
          dataIndex: 'auctionDecreeUrl',
          className: 'w-8',
          render: renderAuctionDecreeUrl,
        },
        {
          title: 'Stav',
          dataIndex: 'state',
          className: 'w-12 text-center',
          sorter: stringSorter('state'),
          render: renderState,
        },
        {
          title: 'Info',
          dataIndex: 'id',
          key: 'info',
          width: 110,
          render: renderInfo,
        },
      ],
      [
        dateFilter,
        filter?.state,
        labelFilter,
        searchFilter,
        renderTitle,
        rangeFilter,
        renderLinkedProperties,
        getColumnOptionFilter,
      ]
    )

    const expandable = useMemo<
      ExpandableConfig<AuctionMonitoringRecord>
    >(() => {
      const allKeys = (data || [])
        ?.filter(record => record?.linkedProperties?.totalCount > 0)
        .map(record => record.id)

      return {
        expandedRowKeys,
        expandedRowRender: record => (
          <AuctionMonitoringLinkedPropertyTableContainer
            auctionId={record.id}
            viewType={viewType}
            activePropertyId={activePropertyId}
          />
        ),
        columnTitle: <ExpandColumnTitle allKeys={allKeys} tableId={tableId} />,
        expandIcon: ({ expanded, record }) => (
          <ExpandIcon
            expanded={expanded}
            recordId={record.id}
            tableId={tableId}
            expandable={record?.linkedProperties?.totalCount > 0}
          />
        ),
        rowExpandable: record => record?.linkedProperties?.totalCount > 0,
      }
    }, [activePropertyId, data, expandedRowKeys, viewType])

    const summary = useCallback(
      () => (
        <AuctionMonitoringTableSummary
          totalColumn={columns.length}
          total={data?.length || 0}
          auctionListingFilter={auctionListingFilter}
        />
      ),
      [auctionListingFilter, columns.length, data?.length]
    )

    return (
      <Table
        rowKey="id"
        size="small"
        rowClassName={rowClassName}
        summary={viewType === 'propertyDetail' ? undefined : summary}
        className="border-t border-gray-200"
        id={tableId}
        columns={columns}
        dataSource={data || []}
        pagination={sparsePagination}
        loading={loading}
        locale={useTableLocale(loading)}
        scroll={scrollX}
        expandable={expandable}
      />
    )
  }
export default AuctionMonitoringTable
